import React, { memo, useMemo } from 'react';
import Button from '@atlaskit/button/new';
import { xcss, Box, media } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type { RowHeight } from '../../types.tsx';
import { TableCell } from '../table-cell/index.tsx';
import {
	useCellBorderStyle,
	useIsAutoUnstickyOnSmallScreenEnabled,
} from '../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

const ROW_HEIGHT: RowHeight = 40;

export const LoadMoreItemsRowNonMemoized = ({
	loadNext,
	isLoadingNext = false,
	columnsCount,
	isStickyColumnsVisible,
	hasCheckboxColumn,
}: {
	loadNext: () => void;
	isLoadingNext?: boolean;
	columnsCount: number;
	isStickyColumnsVisible?: boolean;
	hasCheckboxColumn?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const cellBorderStyle = useCellBorderStyle();
	let isAutoUnstickyOnSmallScreenEnabled = false;
	if (fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line  react-hooks/rules-of-hooks
		isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	}

	const button = (
		<Button appearance="subtle" spacing="compact" isDisabled={isLoadingNext} onClick={loadNext}>
			{isLoadingNext
				? formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.loadingMoreItemsIssueTermRefresh
							: messages.loadingMoreItems,
					)
				: formatMessage(messages.showMore)}
		</Button>
	);

	const cellBorderStyles = useMemo(() => {
		return [
			fg('nin_issue_table_rtl_fix') ? baseCellBorderStylesRTL : baseCellBorderStyles,
			fg('nin_issue_table_rtl_fix')
				? cellBorderLocationStyleMap[cellBorderStyle]
				: cellBorderLocationStyleMapOld[cellBorderStyle],
		];
	}, [cellBorderStyle]);

	const isColumnSticky = isStickyColumnsVisible && fg('issue_table_frozen_columns');
	const stickyButton = fg('nin_issue_table_rtl_fix') ? (
		<Box
			testId="native-issue-table.common.ui.load-more-items-row.load-more-wrapper"
			xcss={
				// eslint-disable-next-line no-nested-ternary
				fg('jira_list_frozen_column_bugfix')
					? isAutoUnstickyOnSmallScreenEnabled
						? stickyContainerResponsiveStylesRTL
						: stickyContainerStylesRTL
					: stickyContainerStylesOldRTL
			}
		>
			{button}
		</Box>
	) : (
		<Box
			testId="native-issue-table.common.ui.load-more-items-row.load-more-wrapper"
			xcss={
				// eslint-disable-next-line no-nested-ternary
				fg('jira_list_frozen_column_bugfix')
					? isAutoUnstickyOnSmallScreenEnabled
						? stickyContainerResponsiveStyles
						: stickyContainerStyles
					: stickyContainerStylesOld
			}
		>
			{button}
		</Box>
	);

	return (
		<Box as="tr" xcss={rowStyles}>
			{isColumnSticky && hasCheckboxColumn === false ? (
				<TableCell
					hasRightShadow
					spacing={firstColumnSpacingStyles}
					border={
						fg('nin_issue_table_rtl_fix') ? firstColumnBorderStyles : firstColumnBorderStylesOld
					}
					isColumnSticky={isColumnSticky}
				>
					{stickyButton}
				</TableCell>
			) : (
				<>
					<TableCell border={cellBorderStyles} isColumnSticky={isColumnSticky} />
					<TableCell border={cellBorderStyles} colSpan={columnsCount}>
						{isColumnSticky ? stickyButton : button}
					</TableCell>
				</>
			)}
		</Box>
	);
};

const LoadMoreItemsRow = componentWithFG(
	'memoise_issuetable_components',
	memo(LoadMoreItemsRowNonMemoized),
	LoadMoreItemsRowNonMemoized,
);
export default LoadMoreItemsRow;

const rowStyles = xcss({
	height: `${ROW_HEIGHT}px`,
	backgroundColor: 'elevation.surface',
});

const baseCellBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderBottomWidth: '0',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderLeftWidth: '0',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightWidth: '0',
});

const baseCellBorderStylesRTL = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderBlockEndWidth: '0',
	borderInlineStartWidth: '0',
	borderInlineEndWidth: '0',
});

const topCellBorderStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: 'border.width',
});

const topCellBorderStyles = xcss({
	borderBlockStartWidth: 'border.width',
});

const noRowCellBorderStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: '0',
});
const noRowCellBorderStyles = xcss({
	borderBlockStartWidth: '0',
});

const cellBorderLocationStyleMapOld = {
	FULL_BORDER: topCellBorderStylesOld,
	NO_COLUMN_BORDER: topCellBorderStylesOld,
	ROW_BORDER_FOR_EXPANDED_TOP_LEVEL_ONLY: noRowCellBorderStylesOld,
};
const cellBorderLocationStyleMap = {
	FULL_BORDER: topCellBorderStyles,
	NO_COLUMN_BORDER: topCellBorderStyles,
	ROW_BORDER_FOR_EXPANDED_TOP_LEVEL_ONLY: noRowCellBorderStyles,
};

const stickyContainerStylesOld = xcss({
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: 'space.600',
	width: 'fit-content',
});

const stickyContainerStylesOldRTL = xcss({
	position: 'sticky',
	insetInlineStart: 'space.600',
	width: 'fit-content',
});

const stickyContainerStyles = xcss({
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: 'space.600',
	width: 'fit-content',
});
const stickyContainerStylesRTL = xcss({
	position: 'sticky',
	insetInlineStart: 'space.600',
	width: 'fit-content',
});
const stickyContainerResponsiveStyles = xcss({
	[media.above.md]: {
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		left: 'space.600',
		width: 'fit-content',
	},
});
const stickyContainerResponsiveStylesRTL = xcss({
	[media.above.md]: {
		position: 'sticky',
		insetInlineStart: 'space.600',
		width: 'fit-content',
	},
});

const firstColumnSpacingStyles = xcss({
	padding: '0',
});

const firstColumnBorderStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightColor: 'color.border',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightStyle: 'solid',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightWidth: 'border.width',
});
const firstColumnBorderStyles = xcss({
	borderInlineEndColor: 'color.border',
	borderInlineEndStyle: 'solid',
	borderInlineEndWidth: 'border.width',
});
